import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const DonutChart = ({ data, size = 120, strokeWidth = 14 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const cx = size / 2, cy = size / 2;
  let offset = 0;
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#f1f5f9" strokeWidth={strokeWidth} />
      {data.map((d, i) => {
        const pct = total > 0 ? d.value / total : 0;
        const dash = pct * circumference;
        const gap = circumference - dash;
        const el = <circle key={i} cx={cx} cy={cy} r={radius} fill="none" stroke={d.color} strokeWidth={strokeWidth} strokeDasharray={`${dash} ${gap}`} strokeDashoffset={-offset} strokeLinecap="round" />;
        offset += dash;
        return el;
      })}
    </svg>
  );
};

const BarChart = ({ data, color }) => {
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '120px', padding: '0 4px' }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', height: '100%', justifyContent: 'flex-end' }}>
          <span style={{ fontSize: '10px', color: '#64748b', fontWeight: '700' }}>{d.count > 0 ? d.count : ''}</span>
          <div style={{ width: '100%', background: d.count > 0 ? color : '#f1f5f9', borderRadius: '4px 4px 0 0', height: `${(d.count/max)*80}px`, minHeight: d.count > 0 ? '4px' : '2px', transition: 'height 0.3s ease' }} />
          <span style={{ fontSize: '9px', color: '#94a3b8', whiteSpace: 'nowrap', transform: 'rotate(-45deg)', transformOrigin: 'center', marginTop: '2px' }}>{d.month}</span>
        </div>
      ))}
    </div>
  );
};

const Dashboard = ({ primary, onNavigate }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trend, setTrend] = useState([]);

  useEffect(() => { loadData(); loadTrend(); }, []);

  const loadTrend = async () => {
    try {
      const res = await axios.get('/api/devices/onboarding-trend');
      if (res.data.success) setTrend(res.data.trend);
    } catch(e) { console.error(e); }
  };

  const loadData = async () => {
    try {
      if (user.role === 'superadmin') {
        const [tenantsRes, devicesRes] = await Promise.all([
          axios.get('/api/tenants'),
          axios.get('/api/devices/all-tenants')
        ]);
        setData({ type: 'superadmin', tenants: tenantsRes.data.tenants, devices: devicesRes.data.devices || [] });
      } else {
        const [devRes, acsRes] = await Promise.all([
          axios.get('/api/devices'),
          axios.get('/api/acs/devices')
        ]);
        const devs = devRes.data.devices || [];
        const acsDevs = acsRes.data.devices || [];
        const pending = acsDevs.filter(d =>
          d._deviceId && d._deviceId._SerialNumber &&
          d._deviceId._Manufacturer !== 'DISCOVERYSERVICE' &&
          !devs.find(dd => dd.serial_number === d._deviceId._SerialNumber)
        );
        setData({ type: 'tenant', devices: devs, pending: pending.length,
          stats: { total: devs.length, online: devs.filter(d => d.status === 'online').length, offline: devs.filter(d => d.status === 'offline').length, pending: pending.length }
        });
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const StatCard = ({ title, value, icon, color, bg, sub, onClick }) => (
    <div onClick={onClick} style={{
      background: 'white', borderRadius: '16px', padding: '24px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9',
      cursor: onClick ? 'pointer' : 'default', transition: 'all 0.2s',
      position: 'relative', overflow: 'hidden'
    }}
      onMouseEnter={e => { if(onClick) { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.1)'; }}}
      onMouseLeave={e => { if(onClick) { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,0.06)'; }}}>
      <div style={{ position: 'absolute', top: 0, right: 0, width: '80px', height: '80px', background: bg, borderRadius: '0 16px 0 80px', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', padding: '12px' }}>
        <span style={{ fontSize: '28px' }}>{icon}</span>
      </div>
      <p style={{ margin: '0 0 8px', fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>{title}</p>
      <h2 style={{ margin: '0 0 4px', fontSize: '38px', fontWeight: '800', color: color, lineHeight: 1 }}>{value ?? '—'}</h2>
      {sub && <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#94a3b8' }}>{sub}</p>}
    </div>
  );

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px', flexDirection: 'column', gap: '16px' }}>
      <div style={{ width: '40px', height: '40px', border: `3px solid ${primary}22`, borderTop: `3px solid ${primary}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
      <p style={{ color: '#94a3b8' }}>Loading...</p>
    </div>
  );

  // SUPERADMIN VIEW
  if (data?.type === 'superadmin') {
    const allDevices = data.devices || [];
    const online = allDevices.filter(d => d.status === 'online').length;
    const offline = allDevices.filter(d => d.status === 'offline').length;
    const activeTenants = data.tenants.filter(t => t.type !== 'platform' && t.is_active);

    return (
      <div>
        <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ margin: '0 0 4px', fontSize: '24px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.5px' }}>Platform Overview 🌐</h1>
          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Fibralink Technologies — ACS Management Platform</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <StatCard title="Active Tenants" value={activeTenants.length} icon="🏢" color={primary} bg={`${primary}10`} sub="ISPs and distributors" />
          <StatCard title="Total Devices" value={allDevices.length} icon="📡" color="#3b82f6" bg="#eff6ff" sub="All registered ONTs" />
          <StatCard title="Online" value={online} icon="🟢" color="#10b981" bg="#ecfdf5" sub="Currently connected" />
          <StatCard title="Offline" value={offline} icon="🔴" color="#ef4444" bg="#fef2f2" sub="Not responding" />
        </div>

        {/* Charts Row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
          {/* Device Status Donut */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>📡 Device Status</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              <div style={{ position: 'relative' }}>
                <DonutChart data={[{value: online, color: '#10b981'}, {value: offline, color: '#ef4444'}, {value: allDevices.length === 0 ? 1 : 0, color: '#f1f5f9'}]} />
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>{allDevices.length}</div>
                  <div style={{ fontSize: '10px', color: '#94a3b8' }}>Total</div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[{label: 'Online', value: online, color: '#10b981'}, {label: 'Offline', value: offline, color: '#ef4444'}].map(s => (
                  <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                    <span style={{ fontSize: '13px', color: '#64748b' }}>{s.label}</span>
                    <span style={{ fontSize: '15px', fontWeight: '700', color: s.color, marginLeft: 'auto' }}>{s.value}</span>
                  </div>
                ))}
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                  {allDevices.length > 0 ? Math.round((online/allDevices.length)*100) : 0}% online rate
                </div>
              </div>
            </div>
          </div>

          {/* Tenant Distribution Donut */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>🏢 Tenant Distribution</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              <div style={{ position: 'relative' }}>
                <DonutChart data={activeTenants.length > 0 ? activeTenants.map((t,i) => ({value: allDevices.filter(d => d.tenant_id?.toString()===t._id?.toString()).length || 1, color: t.branding?.primary_color || ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6'][i%5]})) : [{value:1, color:'#f1f5f9'}]} />
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>{activeTenants.length}</div>
                  <div style={{ fontSize: '10px', color: '#94a3b8' }}>Tenants</div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                {activeTenants.slice(0,4).map((t,i) => {
                  const tDevs = allDevices.filter(d => d.tenant_id?.toString()===t._id?.toString()).length;
                  const colors = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6'];
                  return (
                    <div key={t._id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: t.branding?.primary_color || colors[i%5], flexShrink: 0 }} />
                      <span style={{ fontSize: '12px', color: '#64748b', flex: 1 }}>{t.code}</span>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>{tDevs}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Onboarding Trend - Superadmin */}
        {trend.length > 0 && (
          <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>📈 ONT Onboarding Trend</h3>
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>Last 12 months — All Tenants</span>
            </div>
            <BarChart data={trend} color={primary} />
          </div>
        )}

        {/* Tenant Summary Table */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>🏢 Tenant Summary</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Tenant', 'Code', 'Type', 'Devices', 'Online', 'Offline', 'Status'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #f1f5f9' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.tenants.filter(t => t.type !== 'platform').map((t, i) => {
                  const tDevices = allDevices.filter(d => d.tenant_id && d.tenant_id.toString() === t._id.toString());
                  const tOnline = tDevices.filter(d => d.status === 'online').length;
                  const tOffline = tDevices.filter(d => d.status === 'offline').length;
                  return (
                    <tr key={t._id} style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: t.branding?.primary_color ? t.branding.primary_color + '20' : `${primary}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '15px', color: t.branding?.primary_color || primary }}>
                            {t.name.charAt(0)}
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>{t.name}</div>
                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>{t.branding?.app_name}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: '700', fontSize: '13px', color: '#475569', background: '#f1f5f9', padding: '3px 8px', borderRadius: '6px' }}>{t.code}</span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>{t.type}</span>
                      </td>
                      <td style={{ padding: '14px 16px', fontWeight: '700', fontSize: '18px', color: '#1e293b' }}>{tDevices.length}</td>
                      <td style={{ padding: '14px 16px', fontWeight: '700', color: '#10b981' }}>{tOnline}</td>
                      <td style={{ padding: '14px 16px', fontWeight: '700', color: '#ef4444' }}>{tOffline}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ background: t.is_active ? '#ecfdf5' : '#fef2f2', color: t.is_active ? '#10b981' : '#ef4444', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700' }}>
                          {t.is_active ? '● Active' : '● Inactive'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // TENANT ADMIN VIEW
  const stats = data?.stats || {};
  const total = stats.total || 0;
  const online = stats.online || 0;
  const offline = stats.offline || 0;
  const pending = data?.pending || 0;

  return (
    <div>
      <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 4px', fontSize: '24px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.5px' }}>
          Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 17 ? 'Afternoon' : 'Evening'}, {user?.name?.split(' ')[0]}! 👋
        </h1>
        <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
          {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <StatCard title="Total Devices" value={total} icon="📡" color={primary} bg={`${primary}10`} sub="Registered ONTs" />
        <StatCard title="Online" value={online} icon="🟢" color="#10b981" bg="#ecfdf5" sub="Connected now" />
        <StatCard title="Offline" value={offline} icon="🔴" color="#ef4444" bg="#fef2f2" sub="Not responding" />
        <StatCard title="Pending" value={pending} icon="⏳" color="#f59e0b" bg="#fffbeb" sub="Need registration" />
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        {/* Device Status Donut */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>📡 Device Status</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <DonutChart data={[{value: online, color: '#10b981'}, {value: offline, color: '#ef4444'}, {value: pending, color: '#f59e0b'}, {value: total===0?1:0, color:'#f1f5f9'}]} />
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>{total}</div>
                <div style={{ fontSize: '10px', color: '#94a3b8' }}>Total</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
              {[{label: 'Online', value: online, color: '#10b981'}, {label: 'Offline', value: offline, color: '#ef4444'}, {label: 'Pending', value: pending, color: '#f59e0b'}].map(s => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: '#64748b', flex: 1 }}>{s.label}</span>
                  <span style={{ fontSize: '15px', fontWeight: '700', color: s.color }}>{s.value}</span>
                </div>
              ))}
              <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                {total > 0 ? Math.round((online/total)*100) : 0}% online rate
              </div>
            </div>
          </div>
        </div>

        {/* Onboarding Status Donut */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>📋 Onboarding Status</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <DonutChart data={[{value: total, color: primary}, {value: pending, color: '#f59e0b'}, {value: total+pending===0?1:0, color:'#f1f5f9'}]} />
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>{total+pending > 0 ? Math.round((total/(total+pending))*100) : 0}%</div>
                <div style={{ fontSize: '10px', color: '#94a3b8' }}>Done</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
              {[{label: 'Registered', value: total, color: primary}, {label: 'Pending', value: pending, color: '#f59e0b'}].map(s => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: '#64748b', flex: 1 }}>{s.label}</span>
                  <span style={{ fontSize: '15px', fontWeight: '700', color: s.color }}>{s.value}</span>
                </div>
              ))}
              <button onClick={() => onNavigate && onNavigate('devices')} style={{ marginTop: '8px', padding: '8px 12px', background: `${primary}15`, color: primary, border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '12px' }}>
                View All Devices →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Onboarding Trend Bar Chart */}
      {trend.length > 0 && (
        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>📈 ONT Onboarding Trend</h3>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>Last 12 months</span>
          </div>
          <BarChart data={trend} color={primary} />
        </div>
      )}

      {/* LCO Breakdown */}
      {data?.devices?.length > 0 && (() => {
        const lcoGroups = {};
        data.devices.forEach(d => {
          const key = d.lco_id ? (d.lco_id.name || d.lco_id) : 'Unassigned';
          if (!lcoGroups[key]) lcoGroups[key] = { total: 0, online: 0, offline: 0 };
          lcoGroups[key].total++;
          if (d.status === 'online') lcoGroups[key].online++;
          if (d.status === 'offline') lcoGroups[key].offline++;
        });
        const entries = Object.entries(lcoGroups);
        if (entries.length === 0) return null;
        return (
          <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', marginBottom: '16px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>👤 LCO-wise Breakdown</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['LCO', 'Total', 'Online', 'Offline', 'Health'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', borderBottom: '1px solid #f1f5f9' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map(([lco, s]) => (
                  <tr key={lco} style={{ borderBottom: '1px solid #f8fafc' }}
                    onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background='white'}>
                    <td style={{ padding: '12px 14px', fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>{lco}</td>
                    <td style={{ padding: '12px 14px', fontWeight: '700', color: '#1e293b' }}>{s.total}</td>
                    <td style={{ padding: '12px 14px', fontWeight: '700', color: '#10b981' }}>{s.online}</td>
                    <td style={{ padding: '12px 14px', fontWeight: '700', color: '#ef4444' }}>{s.offline}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ height: '6px', background: '#f1f5f9', borderRadius: '3px', width: '80px' }}>
                        <div style={{ height: '100%', width: `${s.total > 0 ? Math.round((s.online/s.total)*100) : 0}%`, background: '#10b981', borderRadius: '3px' }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Network Health */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>📊 Network Health</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {[
            { label: 'Online Rate', value: total > 0 ? Math.round((online / total) * 100) : 0, color: '#10b981', bg: '#ecfdf5' },
            { label: 'Offline Rate', value: total > 0 ? Math.round((offline / total) * 100) : 0, color: '#ef4444', bg: '#fef2f2' },
            { label: 'Pending Registration', value: total+pending > 0 ? Math.round((pending / (total + pending)) * 100) : 0, color: '#f59e0b', bg: '#fffbeb' },
          ].map(s => (
            <div key={s.label}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>{s.label}</span>
                <span style={{ fontSize: '13px', fontWeight: '700', color: s.color }}>{s.value}%</span>
              </div>
              <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: s.value + '%', background: s.color, borderRadius: '4px', transition: 'width 0.6s ease' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
